import { isCorrect } from "@aybuben/core/exercises/word/logic.ts";
import type { WordStep } from "@aybuben/core/lesson.ts";
import { useEffect } from "react";

import { useCourse } from "../../content/context.tsx";
import { useStrings } from "../../i18n/index.tsx";
import { FOCUS } from "../../theme.ts";
import { FitG, G, Stage } from "../../ui/primitives.tsx";
import { SoundButton, useSound } from "../../ui/sound.tsx";
import { SpeakerRow } from "../../ui/SpeakerRow.tsx";
import { OptionGrid, StepHead } from "../kit.tsx";
import { registerView, type ExerciseViewProps, type FeedbackProps } from "../view-registry.ts";

export function WordView({
	step,
	answer,
	setAnswer,
	status,
	retry,
}: ExerciseViewProps<WordStep, string>) {
	const t = useStrings();
	const course = useCourse();
	const sound = useSound();
	// The `!` is safe: a word step draws only over recipes whose step requires
	// ["audio"], and the validator refuses a speechless item.
	const audio = course.itemById(step.word.id).audio!;
	useEffect(() => {
		if (step.listen) sound.playAuto(audio);
	}, [step.listen, audio, sound]);
	return (
		<div>
			<StepHead
				title={t(step.listen ? "lesson.listenWord" : "lesson.soundOfWord")}
				retry={retry}
				retryNote={t("lesson.retryNote")}
			/>
			<div className="mt-5">
				{step.listen ? (
					<SpeakerRow asset={audio} status={status} />
				) : (
					<Stage>
						<FitG size={54}>{step.word.a}</FitG>
					</Stage>
				)}
			</div>
			<OptionGrid
				options={step.options}
				isRight={(id) => isCorrect(step, id)}
				sel={answer}
				status={status}
				onSel={setAnswer}
			/>
		</div>
	);
}

export function WordFeedback({ step }: FeedbackProps<WordStep>) {
	const course = useCourse();
	const sound = useSound();
	const audio = course.itemById(step.word.id).audio;
	// The word is spoken once the answer is checked, not before: before, it
	// would name the right tile.
	useEffect(() => {
		if (audio) sound.playAuto(audio);
	}, [audio, sound]);
	return (
		<div className="mt-2 flex items-center gap-3">
			<G size={30}>{step.word.a}</G>
			<div className="text-sm leading-snug">
				<button
					type="button"
					onClick={() => audio && sound.play(audio)}
					className={`text-sm font-semibold underline decoration-dotted ${FOCUS}`}
				>
					{step.word.r}
				</button>
				<br />
				{step.word.m}
			</div>
			{audio && <SoundButton asset={audio} className="ml-auto" />}
		</div>
	);
}

registerView<WordStep, string>("word", { View: WordView, Feedback: WordFeedback, action: "check" });
